import React, { useState, useEffect } from 'react';
import { Search, Eye, Ticket as TicketIcon, XCircle as CancelIcon, X, ChevronLeft, ChevronRight, Edit, Plus } from 'lucide-react';
import { TicketListResponse, TicketData } from '../types';
import SearchableDropdown from './SearchableDropdown';
import ServerSideSearchableDropdown from './ServerSideSearchableDropdown';
import { buildApiUrl, API_CONFIG } from '../config/api';

interface User {
  id: number;
  name: string;
  email: string;
}

interface Station {
  id: number;
  code: string;
  name: string;
  line_id: number;
}

interface Region {
  id: number;
  name: string;
  url_code: string;
}

interface Line {
  id: number;
  name: string;
  color: string;
}

const TicketsManager: React.FC = () => {
  const [tickets, setTickets] = useState<TicketListResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingTicketId, setEditingTicketId] = useState<number | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');

  // Create ticket modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [originLineStations, setOriginLineStations] = useState<Station[]>([]);
  const [destinationLineStations, setDestinationLineStations] = useState<Station[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  const [originLineId, setOriginLineId] = useState<number | null>(null);
  const [destinationLineId, setDestinationLineId] = useState<number | null>(null);
  const [originStationId, setOriginStationId] = useState<number | null>(null);
  const [destinationStationId, setDestinationStationId] = useState<number | null>(null);
  const [routeData, setRouteData] = useState<any>(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);

  // Fetch tickets when page, search, or filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTickets();
    }, searchTerm ? 1000 : 0); // Debounce only for search, immediate for page/filter change

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, statusFilter]);

  // Reset to page 1 when search term or filter changes
  useEffect(() => {
    if ((searchTerm || statusFilter !== 'all') && currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add search parameter if search term exists
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.TICKETS}?${params}`));
      const data = await response.json();

      setTickets(data.tickets || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: number) => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.TICKET_BY_ID(ticketId)));
      const data = await response.json();
      setSelectedTicket(data);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
  };

  const cancelTicket = async (ticketId: number) => {
    if (!window.confirm('Are you sure you want to cancel this ticket?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.TICKET_CANCEL(ticketId)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        alert('Ticket cancelled successfully');
        await fetchTickets();
        setSelectedTicket(null);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error cancelling ticket:', error);
      alert('Failed to cancel ticket');
    }
  };

  const updateTicketStatus = async (ticketId: number, status: string) => {
    try {
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.TICKET_UPDATE_STATUS(ticketId, status)),
        { method: 'PUT', headers: { 'Content-Type': 'application/json' } }
      );

      if (response.ok) {
        alert('Ticket status updated successfully');
        await fetchTickets();
        setEditingTicketId(null);
        setNewStatus('');
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      alert('Failed to update ticket status');
    }
  };

  const startEditingStatus = (ticket: TicketListResponse) => {
    setEditingTicketId(ticket.id);
    setNewStatus(ticket.status);
  };

  const fetchUsers = async (searchTerm: string) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('limit', '20'); // Limit results for performance

      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}/?${params.toString()}`));
      const data = await response.json();

      return (data.users || []).map((user: User) => ({
        value: user.id,
        label: user.name,
        sublabel: user.email
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REGIONS));
      const data = await response.json();
      setRegions(data.regions || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchLines = async (regionId: number) => {
    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.LINES}/?region_id=${regionId}`));
      const data = await response.json();
      setLines(data.lines || []);
    } catch (error) {
      console.error('Error fetching lines:', error);
    }
  };

  const fetchStationsForLine = async (lineId: number, isOrigin: boolean) => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STATIONS_BY_LINE(lineId)));
      const stations = await response.json();
      if (isOrigin) {
        setOriginLineStations(stations || []);
      } else {
        setDestinationLineStations(stations || []);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const findRoute = async () => {
    if (!originStationId || !destinationStationId) {
      alert('Please select both origin and destination stations');
      return;
    }

    if (originStationId === destinationStationId) {
      alert('Origin and destination cannot be the same');
      return;
    }

    setLoadingRoute(true);
    try {
      const payload = {
        start: originStationId,
        end: destinationStationId,
        region_id: selectedRegionId
      };

      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.PATHFINDING_SHORTEST_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Server returned ${response.status}`);
      }

      const data = await response.json();

      if (data.shortest_path && data.shortest_path.length > 0) {
        setRouteData(data);
      } else {
        alert('No route found between these stations');
        setRouteData(null);
      }
    } catch (error: any) {
      console.error('Error finding route:', error);
      alert(`Failed to find route: ${error.message}`);
      setRouteData(null);
    } finally {
      setLoadingRoute(false);
    }
  };

  const createTicketForUser = async () => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    if (!routeData) {
      alert('Please find a route first');
      return;
    }

    setCreatingTicket(true);
    try {
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.ADMIN_BOOK_TICKET(selectedUserId)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            route_data: routeData
          }),
        }
      );

      if (response.ok) {
        alert('Ticket created successfully!');
        await fetchTickets();
        closeCreateModal();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const openCreateModal = async () => {
    setShowCreateModal(true);
    await fetchRegions();
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setSelectedUserId(null);
    setSelectedRegionId(null);
    setOriginLineId(null);
    setDestinationLineId(null);
    setOriginStationId(null);
    setDestinationStationId(null);
    setRouteData(null);
    setLines([]);
    setOriginLineStations([]);
    setDestinationLineStations([]);
  };


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'used':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <TicketIcon className="h-6 w-6 mr-2 text-admin-600" />
          Tickets Management
        </h2>
        <button
          onClick={openCreateModal}
          className="admin-btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Ticket
        </button>
      </div>

      {/* Filters */}
      <div className="admin-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by TKT-****, user email, or user name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-input"
          >
            <option value="all">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="used">Used</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segments</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">#{ticket.id}</div>
                    <div className="text-xs text-gray-500 font-mono">{ticket.ticket_unique_string}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ticket.user_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ticket.segment_count} segment{ticket.segment_count !== 1 ? 's' : ''}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ฿{ticket.total_amount.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingTicketId === ticket.id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="confirmed">Confirmed</option>
                          <option value="used">Used</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="expired">Expired</option>
                        </select>
                        <button
                          onClick={() => updateTicketStatus(ticket.id, newStatus)}
                          className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingTicketId(null);
                            setNewStatus('');
                          }}
                          className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <button
                          onClick={() => startEditingStatus(ticket)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit status"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(ticket.valid_until).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => fetchTicketDetails(ticket.id)}
                      className="text-admin-600 hover:text-admin-900"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {ticket.status === 'confirmed' && (
                      <button
                        onClick={() => cancelTicket(ticket.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Cancel Ticket"
                      >
                        <CancelIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Ticket Details</h3>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* QR Code */}
              {selectedTicket.qr_code && (
                <div className="flex justify-center">
                  <img
                    src={selectedTicket.qr_code}
                    alt="Ticket QR Code"
                    className="w-48 h-48 border-2 border-gray-300 rounded-lg"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Ticket ID</label>
                  <div className="text-sm text-gray-900">#{selectedTicket.id}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Unique String</label>
                  <div className="text-sm text-gray-900 font-mono">{selectedTicket.ticket_unique_string}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Amount</label>
                  <div className="text-sm text-gray-900">฿{selectedTicket.total_amount.toFixed(2)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Valid From</label>
                  <div className="text-sm text-gray-900">{new Date(selectedTicket.valid_from).toLocaleString()}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Valid Until</label>
                  <div className="text-sm text-gray-900">{new Date(selectedTicket.valid_until).toLocaleString()}</div>
                </div>
              </div>

              {selectedTicket.segments && selectedTicket.segments.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Journey Segments</label>
                  <div className="space-y-2">
                    {selectedTicket.segments.map((segment, index) => (
                      <div key={segment.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs font-semibold bg-admin-600 text-white px-2 py-0.5 rounded">
                                {segment.segment_order + 1}
                              </span>
                              <span className="font-medium">{segment.from_station_name} → {segment.to_station_name}</span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">{segment.line_name}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancel Button */}
              {selectedTicket.status === 'confirmed' && (
                <div className="pt-4 border-t">
                  <button
                    onClick={() => cancelTicket(selectedTicket.id)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Cancel This Ticket
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create Ticket for User</h3>
              <button
                onClick={closeCreateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* User Selection */}
              <ServerSideSearchableDropdown
                label="Select User"
                required
                fetchOptions={fetchUsers}
                value={selectedUserId}
                onChange={(value) => setSelectedUserId(Number(value))}
                placeholder="Search for a user..."
                minSearchLength={2}
                debounceMs={300}
              />

              {/* Region Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Region *
                </label>
                <select
                  value={selectedRegionId || ''}
                  onChange={(e) => {
                    const regionId = Number(e.target.value);
                    setSelectedRegionId(regionId);
                    setOriginLineId(null);
                    setDestinationLineId(null);
                    setOriginStationId(null);
                    setDestinationStationId(null);
                    setRouteData(null);
                    setOriginLineStations([]);
                    setDestinationLineStations([]);
                    if (regionId) {
                      fetchLines(regionId);
                    } else {
                      setLines([]);
                    }
                  }}
                  className="admin-input"
                >
                  <option value="">Choose a region...</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Origin Line */}
              <SearchableDropdown
                label="Origin Line"
                required
                options={lines.map(line => ({
                  value: line.id,
                  label: line.name,
                  sublabel: line.color ? `Color: ${line.color}` : undefined
                }))}
                value={originLineId}
                onChange={(value) => {
                  const lineId = Number(value);
                  setOriginLineId(lineId);
                  setOriginStationId(null);
                  setRouteData(null);
                  if (lineId) {
                    fetchStationsForLine(lineId, true);
                  } else {
                    setOriginLineStations([]);
                  }
                }}
                placeholder="Choose origin line..."
                disabled={!selectedRegionId}
              />

              {/* Destination Line */}
              <SearchableDropdown
                label="Destination Line"
                required
                options={lines.map(line => ({
                  value: line.id,
                  label: line.name,
                  sublabel: line.color ? `Color: ${line.color}` : undefined
                }))}
                value={destinationLineId}
                onChange={(value) => {
                  const lineId = Number(value);
                  setDestinationLineId(lineId);
                  setDestinationStationId(null);
                  setRouteData(null);
                  if (lineId) {
                    fetchStationsForLine(lineId, false);
                  } else {
                    setDestinationLineStations([]);
                  }
                }}
                placeholder="Choose destination line..."
                disabled={!selectedRegionId}
              />

              {/* Origin Station */}
              <SearchableDropdown
                label="Origin Station"
                required
                options={originLineStations.map(station => ({
                  value: station.id,
                  label: station.name,
                  sublabel: station.code
                }))}
                value={originStationId}
                onChange={(value) => {
                  setOriginStationId(Number(value));
                  setRouteData(null);
                }}
                placeholder="Choose origin station..."
                disabled={!originLineId}
              />

              {/* Destination Station */}
              <SearchableDropdown
                label="Destination Station"
                required
                options={destinationLineStations.map(station => ({
                  value: station.id,
                  label: station.name,
                  sublabel: station.code
                }))}
                value={destinationStationId}
                onChange={(value) => {
                  setDestinationStationId(Number(value));
                  setRouteData(null);
                }}
                placeholder="Choose destination station..."
                disabled={!destinationLineId}
              />

              {/* Find Route Button */}
              <button
                onClick={findRoute}
                disabled={!originStationId || !destinationStationId || loadingRoute}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingRoute ? 'Finding Route...' : 'Find Route'}
              </button>

              {/* Route Preview */}
              {routeData && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">Route Found!</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Total Cost:</span> ฿{routeData.cost?.toFixed(2)}
                    </div>
                    <div>
                      <span className="font-medium">Stations:</span> {routeData.station_names?.join(' → ')}
                    </div>
                    {routeData.interchange_details && routeData.interchange_details.length > 0 && (
                      <div>
                        <span className="font-medium">Interchanges:</span>
                        <ul className="list-disc list-inside ml-2">
                          {routeData.interchange_details.map((interchange: any, index: number) => (
                            <li key={index}>
                              {interchange.station_name}: {interchange.from_line} → {interchange.to_line}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={createTicketForUser}
                  disabled={!selectedUserId || !routeData || creatingTicket}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingTicket ? 'Creating Ticket...' : 'Create Ticket'}
                </button>
                <button
                  onClick={closeCreateModal}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200 mt-6">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} to{' '}
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.ceil(totalCount / itemsPerPage) }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === Math.ceil(totalCount / itemsPerPage) ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-admin-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / itemsPerPage), currentPage + 1))}
              disabled={currentPage === Math.ceil(totalCount / itemsPerPage)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsManager;